import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import {
  ChannelThreadId,
  DraftMessageId,
  MessageId,
  SupportTicketId,
} from "../ids/definitions";
import { MongoUserId } from "../ids/mongo-ids";
import { MessageContentType } from "../models/shopper-ai/conversation/add-message-input";
import {
  StartBulkSupportTicketActionByFilterInput,
  StartBulkSupportTicketActionByFilterResponse,
  StartBulkSupportTicketActionInput,
  StartBulkSupportTicketActionResponse,
} from "../models/shopper-ai/conversation/bulk-support-ticket-action";
import type {
  AiCopilotDraftResponse,
  AutopilotSendDraftResponse,
  MerchantDraftResponse,
} from "../models/shopper-ai/conversation/draft";
import {
  DraftMessageCandidateState,
  DraftMessageStatus,
  EmailDraftChannel,
  EmailDraftChannelData,
  NonEmailDraftChannel,
  SaveDraftInput,
  SendMessagePayload,
} from "../models/shopper-ai/conversation/draft";
import {
  Message,
  MessageCursor,
  MessageSenderType,
} from "../models/shopper-ai/conversation/message";
import { Attachment } from "../models/shopper-ai/conversation/message/attachment";
import { MessageSendBlockedError } from "../models/shopper-ai/conversation/message/block-rule-error";
import {
  CreateDraftSupportTicketInput,
  CreateDraftSupportTicketResponse,
  CreateDraftThreadInput,
  CreateDraftThreadResponse,
  PublishDraftSupportTicketInput,
  StartSupportTicketInput,
  StartSupportTicketResponse,
  SupportTicketListItemResponse,
  SupportTicketResponse,
  SupportTicketThreadResponse,
  UpdateSupportTicketInput,
} from "../models/shopper-ai/conversation/support-ticket";
import { ChannelThreadCursor } from "../models/shopper-ai/conversation/thread";
import { SupportTicketTimelineEventResponse } from "../models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  CursorPaginatedResponse,
  CursorPaginationParams,
  DEFAULT_LIMIT,
  MAX_LIMIT,
  SearchAfterCursor,
} from "../schema/pagination";

const SupportTicketDraftResponseBase = {
  id: DraftMessageId,
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
  inReplyToMessageId: Schema.OptionFromUndefinedOr(MessageId),
  senderMongoUserId: Schema.NullOr(MongoUserId),
  senderType: MessageSenderType,
  contentType: MessageContentType,
  content: Schema.String,
  plainContent: Schema.NullOr(Schema.String),
  attachments: Schema.Array(Attachment),
  mentionedMongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
  status: DraftMessageStatus,
  version: Schema.Number,
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
};

const EmailSupportTicketDraftResponseFields = {
  channel: EmailDraftChannel,
  channelData: EmailDraftChannelData,
};

const NonEmailSupportTicketDraftResponseFields = {
  channel: NonEmailDraftChannel,
  channelData: Schema.Null,
};

const SupportTicketMerchantDraftResponseBase = {
  ...SupportTicketDraftResponseBase,
  kind: Schema.Literal("merchant"),
  candidateState: Schema.Null,
};
const EmailSupportTicketMerchantDraftResponse = Schema.Struct({
  ...SupportTicketMerchantDraftResponseBase,
  ...EmailSupportTicketDraftResponseFields,
});
const NonEmailSupportTicketMerchantDraftResponse = Schema.Struct({
  ...SupportTicketMerchantDraftResponseBase,
  ...NonEmailSupportTicketDraftResponseFields,
});
export const SupportTicketMerchantDraftResponse = Schema.Union(
  EmailSupportTicketMerchantDraftResponse,
  NonEmailSupportTicketMerchantDraftResponse,
);
export type SupportTicketMerchantDraftResponse =
  typeof SupportTicketMerchantDraftResponse.Type;

const SupportTicketAiCopilotDraftResponseBase = {
  ...SupportTicketDraftResponseBase,
  kind: Schema.Literal("ai_copilot"),
  candidateState: DraftMessageCandidateState,
};
const EmailSupportTicketAiCopilotDraftResponse = Schema.Struct({
  ...SupportTicketAiCopilotDraftResponseBase,
  ...EmailSupportTicketDraftResponseFields,
});
const NonEmailSupportTicketAiCopilotDraftResponse = Schema.Struct({
  ...SupportTicketAiCopilotDraftResponseBase,
  ...NonEmailSupportTicketDraftResponseFields,
});
export const SupportTicketAiCopilotDraftResponse = Schema.Union(
  EmailSupportTicketAiCopilotDraftResponse,
  NonEmailSupportTicketAiCopilotDraftResponse,
);
export type SupportTicketAiCopilotDraftResponse =
  typeof SupportTicketAiCopilotDraftResponse.Type;

const SupportTicketAutopilotSendDraftResponseBase = {
  ...SupportTicketDraftResponseBase,
  kind: Schema.Literal("autopilot_send"),
  candidateState: Schema.Null,
};
const EmailSupportTicketAutopilotSendDraftResponse = Schema.Struct({
  ...SupportTicketAutopilotSendDraftResponseBase,
  ...EmailSupportTicketDraftResponseFields,
});
const NonEmailSupportTicketAutopilotSendDraftResponse = Schema.Struct({
  ...SupportTicketAutopilotSendDraftResponseBase,
  ...NonEmailSupportTicketDraftResponseFields,
});
export const SupportTicketAutopilotSendDraftResponse = Schema.Union(
  EmailSupportTicketAutopilotSendDraftResponse,
  NonEmailSupportTicketAutopilotSendDraftResponse,
);
export type SupportTicketAutopilotSendDraftResponse =
  typeof SupportTicketAutopilotSendDraftResponse.Type;

export const SupportTicketDraftResponse = Schema.Union(
  SupportTicketMerchantDraftResponse,
  SupportTicketAiCopilotDraftResponse,
  SupportTicketAutopilotSendDraftResponse,
);
export type SupportTicketDraftResponse = typeof SupportTicketDraftResponse.Type;

export const SupportTicketDraftsResponse = Schema.Struct({
  drafts: Schema.Array(SupportTicketDraftResponse),
});
export type SupportTicketDraftsResponse =
  typeof SupportTicketDraftsResponse.Type;

export const SupportTicketSummaryResponse = Schema.Struct({
  summary: Schema.NullOr(Schema.String),
});
export type SupportTicketSummaryResponse =
  typeof SupportTicketSummaryResponse.Type;

export const SupportTicketListResponse = Schema.Struct({
  items: Schema.Array(SupportTicketListItemResponse),
  totalCount: Schema.Number,
  nextCursor: Schema.optional(SearchAfterCursor),
});
export type SupportTicketListResponse = typeof SupportTicketListResponse.Type;

export const SupportTicketListQueryParams = Schema.Struct({
  limit: Schema.optionalWith(
    Schema.NumberFromString.pipe(
      Schema.int(),
      Schema.positive(),
      Schema.lessThanOrEqualTo(MAX_LIMIT),
    ),
    { default: () => DEFAULT_LIMIT },
  ),
  offset: Schema.optionalWith(
    Schema.NumberFromString.pipe(Schema.int(), Schema.nonNegative()),
    { default: () => 0 },
  ),
  searchAfter: Schema.optional(
    Schema.compose(
      Schema.StringFromUriComponent,
      Schema.parseJson(SearchAfterCursor),
    ),
  ),
  filterGroup: Schema.optional(Schema.String),
  sorts: Schema.optional(Schema.String),
  includeTotalCount: Schema.optional(Schema.BooleanFromString),
  includeHighlights: Schema.optional(Schema.BooleanFromString),
});
export type SupportTicketListQueryParams =
  typeof SupportTicketListQueryParams.Type;

export const SupportTicketDetailResponse = Schema.Struct({
  supportTicket: SupportTicketListItemResponse,
  threads: CursorPaginatedResponse(
    SupportTicketThreadResponse,
    ChannelThreadCursor,
  ),
  messages: CursorPaginatedResponse(Message, MessageCursor),
  drafts: SupportTicketDraftsResponse,
  summary: SupportTicketSummaryResponse,
});
export type SupportTicketDetailResponse =
  typeof SupportTicketDetailResponse.Type;

export const AcceptAiCopilotDraftResponse = SupportTicketAiCopilotDraftResponse;
export type AcceptAiCopilotDraftResponse =
  typeof AcceptAiCopilotDraftResponse.Type;

export const DeclineAiCopilotDraftResponse =
  SupportTicketAiCopilotDraftResponse;
export type DeclineAiCopilotDraftResponse =
  typeof DeclineAiCopilotDraftResponse.Type;

export type SupportTicketDraftResponseFromDraft<
  T extends
    | MerchantDraftResponse
    | AiCopilotDraftResponse
    | AutopilotSendDraftResponse,
> = T extends unknown
  ? Omit<T, "topicId"> & { supportTicketId: SupportTicketId }
  : never;

export const UndoSupportTicketMergeInput = Schema.Struct({
  relationshipId: Schema.optional(Schema.String),
  sourceSupportTicketId: SupportTicketId,
  destinationSupportTicketId: SupportTicketId,
});
export type UndoSupportTicketMergeInput =
  typeof UndoSupportTicketMergeInput.Type;

export const UndoSupportTicketMergeResponse = Schema.Struct({
  undone: Schema.Literal(true),
});
export type UndoSupportTicketMergeResponse =
  typeof UndoSupportTicketMergeResponse.Type;

/**
 * Mark/unmark spam endpoint result. Empty payload — callers refresh from
 * the support-ticket list / detail queries which will reflect the
 * status + tag changes.
 */
export const MarkSupportTicketSpamResponse = Schema.Struct({});
export type MarkSupportTicketSpamResponse =
  typeof MarkSupportTicketSpamResponse.Type;

export const SupportTicketGroupBase = HttpApiGroup.make("supportTicket")
  .add(
    endpoint.merchant
      .post("startSupportTicket", "/support-tickets/start")
      .setPayload(StartSupportTicketInput)
      .addSuccess(StartSupportTicketResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createDraftSupportTicket", "/support-tickets/draft")
      .setPayload(CreateDraftSupportTicketInput)
      .addSuccess(CreateDraftSupportTicketResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "createSupportTicketDraftThread",
        "/support-tickets/:id/threads/draft",
      )
      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setPayload(CreateDraftThreadInput)
      .addSuccess(CreateDraftThreadResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("publishDraftSupportTicket", "/support-tickets/:id/publish")
      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setPayload(PublishDraftSupportTicketInput)
      .addSuccess(StartSupportTicketResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("listSupportTickets", "/support-tickets")
      .setUrlParams(SupportTicketListQueryParams)
      .addSuccess(SupportTicketListResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicket", "/support-tickets/:id")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(SupportTicketListItemResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketDetail", "/support-tickets/:id/detail")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(SupportTicketDetailResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketThreads", "/support-tickets/:id/threads")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setUrlParams(CursorPaginationParams(ChannelThreadCursor))
      .addSuccess(
        CursorPaginatedResponse(
          SupportTicketThreadResponse,
          ChannelThreadCursor,
        ),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketMessages", "/support-tickets/:id/messages")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setUrlParams(CursorPaginationParams(MessageCursor))
      .addSuccess(CursorPaginatedResponse(Message, MessageCursor))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateSupportTicket", "/support-tickets/:id")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setPayload(UpdateSupportTicketInput)
      .addSuccess(SupportTicketResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("startBulkSupportTicketAction", "/support-tickets/bulk")

      .setPayload(StartBulkSupportTicketActionInput)
      .addSuccess(StartBulkSupportTicketActionResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "startBulkSupportTicketActionByFilter",
        "/support-tickets/bulk/by-filter",
      )

      .setPayload(StartBulkSupportTicketActionByFilterInput)
      .addSuccess(StartBulkSupportTicketActionByFilterResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("undoSupportTicketMerge", "/support-tickets/merge/undo")

      .setPayload(UndoSupportTicketMergeInput)
      .addSuccess(UndoSupportTicketMergeResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("markSupportTicketSpam", "/support-tickets/:id/spam")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(MarkSupportTicketSpamResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("unmarkSupportTicketSpam", "/support-tickets/:id/spam")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(MarkSupportTicketSpamResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("saveSupportTicketDraft", "/support-tickets/:id/drafts")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setPayload(SaveDraftInput)
      .addSuccess(SupportTicketMerchantDraftResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketDraft", "/support-tickets/:id/drafts")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setUrlParams(
        Schema.Struct({
          threadId: ChannelThreadId,
          inReplyToMessageId: Schema.optional(MessageId),
        }),
      )
      .addSuccess(Schema.NullOr(SupportTicketMerchantDraftResponse))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketDrafts", "/support-tickets/:id/drafts/all")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(SupportTicketDraftsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteSupportTicketDraft", "/support-tickets/:id/drafts/:draftId")

      .setPath(Schema.Struct({ id: SupportTicketId, draftId: DraftMessageId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "acceptAiCopilotDraft",
        "/support-tickets/:id/ai-drafts/:draftId/accept",
      )

      .setPath(Schema.Struct({ id: SupportTicketId, draftId: DraftMessageId }))
      .addSuccess(AcceptAiCopilotDraftResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "declineAiCopilotDraft",
        "/support-tickets/:id/ai-drafts/:draftId/decline",
      )

      .setPath(Schema.Struct({ id: SupportTicketId, draftId: DraftMessageId }))
      .addSuccess(DeclineAiCopilotDraftResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("sendSupportTicketMessage", "/support-tickets/:id/messages")

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .setPayload(SendMessagePayload)
      .addSuccess(Schema.Struct({ workflowId: Schema.String }))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(MessageSendBlockedError)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "cancelSend",
        "/support-tickets/:id/messages/send/:workflowId/cancel",
      )
      .setPath(
        Schema.Struct({ id: SupportTicketId, workflowId: Schema.String }),
      )
      .addSuccess(Schema.Struct({ cancelled: Schema.Literal(true) }))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getSupportTicketTimelineEvents",
        "/support-tickets/:id/timeline-events",
      )

      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(
        Schema.Struct({
          items: Schema.Array(SupportTicketTimelineEventResponse),
        }),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getSupportTicketSummary", "/support-tickets/:id/summary")
      .setPath(Schema.Struct({ id: SupportTicketId }))
      .addSuccess(SupportTicketSummaryResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  );
